import React, { createContext, useRef } from 'react';
import classNames from 'classnames';
import { useCheckboxGroup, useCheckboxGroupItem } from '@react-aria/checkbox';
import { useFocusRing } from '@react-aria/focus';
import { useVisuallyHidden } from '@react-aria/visually-hidden';
import { Icon, COLORS, Tooltip } from 'Permafrost/index';
import { StyledPillCheckGroupBase } from './PillCheckGroupBase.styles';
import { StyledPill } from './Pill.styles';
import { useCheckboxGroupState } from 'react-stately';
export const PillCheckGroupContext = createContext({});
/**
 * Renders a group of 'pill checkboxes'.
 *
 * A group label must be included: either pass a string or markup into the
 * `label` prop, or include an `aria-label` or `aria-labelledby` attribute.
 */
export function PillCheckGroupBase(props) {
    const { id, children, className, disableGroup = false, isReadOnly = false, label, enforceSingleValue = false, onChange, verticalOrientation, values, } = props;
    const checkboxGroupProps = {
        value: values,
        isDisabled: disableGroup,
        isReadOnly: isReadOnly,
        onChange: (newValue) => {
            if (isReadOnly || disableGroup) {
                return;
            }
            if (enforceSingleValue && newValue.length > 1) {
                onChange([newValue[newValue.length - 1]]);
            }
            else {
                onChange(newValue);
            }
        },
    };
    const state = useCheckboxGroupState(checkboxGroupProps);
    const { groupProps, labelProps } = useCheckboxGroup(props, state);
    return (React.createElement(StyledPillCheckGroupBase, Object.assign({ id: id, className: classNames(className, {
            'PillCheckGroupBase--horizontal': verticalOrientation !== true,
            'PillCheckGroupBase--disabled': disableGroup,
        }), "data-cy": props['data-cy'], "data-testid": props['data-testid'] }, groupProps),
        label && (React.createElement("div", Object.assign({ className: "PillCheckGroup__group-label" }, labelProps), label)),
        React.createElement(PillCheckGroupContext.Provider, { value: state }, children)));
}
export function Pill(props) {
    const { id, caption, captionColor, children, className, fixedWidth, isDisabled = false, isSelected, pillTextColor, tooltip, iconName = 'check-circle', iconColor = COLORS.fieldPromptInfo, } = props;
    const state = React.useContext(PillCheckGroupContext);
    const inputRef = useRef(null);
    const { inputProps } = useCheckboxGroupItem(props, state, inputRef);
    const { isFocusVisible, focusProps } = useFocusRing();
    const { visuallyHiddenProps } = useVisuallyHidden();
    const checkMeasure = 12;
    return (React.createElement(StyledPill, { id: id, "$captionColor": captionColor || 'inherit', className: classNames({ 'Pill__label--checked': isSelected, isDisabled }, className), "data-cy": props['data-cy'], "data-testid": props['data-testid'], "data-for": id, "data-tip": true, "$fixedWidth": fixedWidth, "$pillTextColor": pillTextColor || 'inherit' },
        React.createElement("input", Object.assign({ ref: inputRef, type: "checkbox" }, focusProps, inputProps, visuallyHiddenProps)),
        React.createElement("div", { className: classNames(className, {
                'Pill__pill-ellipse--focusRing': isFocusVisible === true,
                'Pill__pill-ellipse--selected': isSelected === true,
            }, 'pill-ellipse') },
            React.createElement("span", { className: "pill-text" }, children),
            isSelected && (React.createElement(Icon, { name: iconName, className: "pill-icon", size: [checkMeasure], fill: iconColor }))),
        caption && React.createElement("span", { className: "caption" }, caption),
        tooltip ? React.createElement(Tooltip, { for: id }, tooltip) : null));
}
//# sourceMappingURL=PillCheckGroupBase.js.map